import React from 'react';
import { Link } from 'react-router-dom';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import {
    Badge,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCellProps,
    TableRow,
} from '@freelancelabs/components';
import { EyeOutline } from '@freelancelabs/icons';

import ListCard from '../../components/ListCard';
import { RoleScope } from '../../graphql/__generated__/schema';
import { PortalModulesDocument, RoleSummaryFragment, RolesDocument } from '../../graphql/__generated__/types';
import { buildRightsGroups } from './grouping';

const rightsCountLabel = (count: number) => ngettext(msgid`${count} right`, `${count} rights`, count);

/**
 * The roles referential: module 2 of the mockup, list view (§6.1).
 *
 * Both scopes in one table rather than two: a portal role and a back-office role are
 * the same object, and splitting them would hide that seven rows is the whole
 * referential. The scope is a column.
 *
 * « Utilisateurs associés » is a **link**, as in the mockup: it lands on the users
 * list already filtered on the role, which is the question one asks in front of that
 * number. The count itself comes from the role: it counts every membership, while
 * the list it opens is bounded by the accounts the reader may see. A back-office user
 * with a partial portfolio may therefore land on fewer rows than the number
 * announced: that is the perimeter doing its job, not a mismatch to fix.
 *
 * Creating a role is not open: a role is global, so writing one would add `role_permission`
 * rows applying to every client account at once. The button says so rather than
 * disappearing: a missing button reads as an oversight.
 */
const RolesPage = () => {
    const { data, loading, error } = useQuery(RolesDocument);
    // The module referential names and orders the badges below. It is not fetched for
    // decoration: what it avoids is this file holding a second copy of the menu.
    const { data: modulesData } = useQuery(PortalModulesDocument);

    const roles: RoleSummaryFragment[] = [...(data?.portal ?? []), ...(data?.backoffice ?? [])];
    const modules = modulesData?.portalModules ?? [];

    const columns: TableHeaderCellProps[] = [
        { id: 'name', label: c('Roles column').t`Role name`, minWidth: 200 },
        { id: 'scope', label: c('Roles column').t`Scope`, minWidth: 100 },
        { id: 'members', label: c('Roles column').t`Associated users`, minWidth: 150 },
        { id: 'rights', label: c('Roles column').t`Rights granted`, minWidth: 330 },
        // No « Créé le » here: the mockup keeps that date for the sheet, and a
        // referential of seven rows is not read by date.
        { id: 'actions', label: '', minWidth: 130, textAlign: 'end' },
    ];

    /**
     * Modules touched by a role, in menu order, administration last. Says what a role is
     * about before anyone opens it.
     *
     * **Attention, same axis as the matrix of the sheet** (PCG-99): a summary by capability next
     * to a matrix by module would class the same rights two ways on two screens, and the
     * reader would have no way of telling which one is the referential.
     *
     * While the module referential is still in flight there is nothing to name a badge
     * with, so the row shows its rights counter alone rather than a set of headings this
     * file would have had to invent. It fills in on its own when the query answers.
     */
    const modulesOf = (role: RoleSummaryFragment) =>
        modules.length === 0
            ? []
            : buildRightsGroups((role.permissions ?? []).filter(Boolean), modules).filter(
                  (group) => group.permissions.length > 0
              );

    const roleCount = roles.length;

    return (
        <section className="flex flex-col gap-5">
            <header className="flex flex-wrap items-start gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-text-900">{c('Roles').t`Roles`}</h1>
                    <p className="mt-1 max-w-2xl text-xs text-text-600">
                        {c('Roles').t`Sets of functional rights, reusable across users.`}
                    </p>
                </div>
                <Button
                    className="ml-auto"
                    category="default"
                    disabled
                    title={c('Roles').t`Creating a role is not open yet: a role is shared by every client account.`}
                >
                    {c('Roles').t`Create a role`}
                </Button>
            </header>

            {error ? (
                <p className="text-danger-600" role="alert">
                    {c('Roles').t`This referential could not be loaded.`}
                </p>
            ) : (
                <ListCard
                    label={c('Roles').t`Roles referential`}
                    footer={
                        !loading &&
                        roleCount > 0 && (
                            <span>{ngettext(msgid`${roleCount} role`, `${roleCount} roles`, roleCount)}</span>
                        )
                    }
                >
                    <>
                        <Table>
                            <TableHeader resizable cells={columns} />
                            <TableBody loading={loading} loadingRows={5} loadingColumns={columns.length}>
                                {roles.map((role) => {
                                    const users = role.membershipCount ?? 0;
                                    return (
                                        <TableRow key={role.id}>
                                            {/* Plain, as in the mockup: the row already carries its
                                                way in, and a name that is a link asks to be clicked
                                                twice. The description belongs to the sheet: here it
                                                was cut mid-word by the column. */}
                                            <TableCell className="!whitespace-normal !break-words text-text-900">
                                                {/* Weight on the text, not on the cell: the card
                                                    sets the cells to normal with a rule a single
                                                    class cannot outweigh. */}
                                                <span className="font-medium">{role.name}</span>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    size="small"
                                                    category={role.scope === RoleScope.Backoffice ? 'info' : 'default'}
                                                >
                                                    {role.scope === RoleScope.Backoffice
                                                        ? c('Role scope').t`Back-office`
                                                        : c('Role scope').t`Portal`}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {users === 0 ? (
                                                    <span className="text-text-500">{c('Roles').t`No user`}</span>
                                                ) : (
                                                    <Link to={`/utilisateurs?role=${role.id}`} className="link text-sm">
                                                        {c('Roles').ngettext(
                                                            msgid`${users} user`,
                                                            `${users} users`,
                                                            users
                                                        )}
                                                    </Link>
                                                )}
                                            </TableCell>
                                            <TableCell className="!whitespace-normal py-2">
                                                <span className="flex flex-wrap items-center gap-1.5">
                                                    {modulesOf(role).map((group) => (
                                                        <span
                                                            key={group.key}
                                                            className="rounded-md border border-border-norm bg-background-weak px-1.5 py-0.5 font-mono text-[11px] text-text-600"
                                                        >
                                                            {group.label}
                                                        </span>
                                                    ))}
                                                    <span className="rounded-full border border-border-norm bg-background-weak px-2 py-0.5 font-mono text-[11px] text-text-500">
                                                        {rightsCountLabel(role.permissions?.length ?? 0)}
                                                    </span>
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-end">
                                                <Button
                                                    to={`/roles/${role.id}`}
                                                    category="weak"
                                                    size="small"
                                                    icon
                                                    aria-label={c('Roles').t`View the role`}
                                                    title={c('Roles').t`View the role`}
                                                >
                                                    <EyeOutline size={16} />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        {!loading && roleCount === 0 && (
                            <p className="fl-table-empty" aria-live="polite">
                                {c('Roles').t`No role in the referential.`}
                            </p>
                        )}
                    </>
                </ListCard>
            )}
        </section>
    );
};

export default RolesPage;
